import { useEffect, useState } from "react";
import { X, Minus, Plus, Trash2, CreditCard, Loader2, Calculator } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "../../context/CartContext";
import { api, formatBRL, resolveImageUrl } from "../../lib/api";
import { WhatsAppIcon, buildWhatsAppLink } from "./Header";
import CheckoutMP from "./CheckoutMP";
import { InstallmentSimulator } from "./Simulators";

export default function CartDrawer({ whatsappNumber }) {
  const { items, isOpen, closeCart, updateQty, removeItem, clearCart, total } = useCart();
  const [customerName, setCustomerName] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [mpConfig, setMpConfig] = useState({ mercadopago_enabled: false, mp_public_key: "" });
  const [mpOrder, setMpOrder] = useState(null);
  const [cep, setCep] = useState("");
  const [shippingOptions, setShippingOptions] = useState([]);
  const [shipping, setShipping] = useState(null);
  const [shippingMode, setShippingMode] = useState(null);
  const [shippingLoading, setShippingLoading] = useState(false);
  const [couponInput, setCouponInput] = useState("");
  const [coupon, setCoupon] = useState(null);
  const [showSim, setShowSim] = useState(false);

  const discount = coupon
    ? coupon.type === "percent"
      ? Math.round(total * coupon.value) / 100
      : Math.min(coupon.value, total)
    : 0;
  const grandTotal = Math.max(0, Math.round((total - discount) * 100) / 100) + (shipping?.price || 0);

  useEffect(() => {
    if (isOpen) {
      api.get("/payments/config").then((res) => setMpConfig(res.data)).catch(() => {});
    }
  }, [isOpen]);

  // Mudou a sacola → frete precisa ser recalculado
  useEffect(() => {
    setShipping(null);
    setShippingOptions([]);
    setShippingMode(null);
  }, [total]);

  if (!isOpen) return null;

  const calcShipping = async () => {
    const clean = cep.replace(/\D/g, "");
    if (clean.length !== 8) {
      toast.error("Informe o CEP com 8 dígitos");
      return;
    }
    setShippingLoading(true);
    setShipping(null);
    try {
      const { data } = await api.post("/shipping/quote", { destination_cep: clean, order_value: total });
      if (data.mode === "live") {
        setShippingOptions(data.options);
        setShippingMode("live");
        if (data.options.length === 0) toast.info("Sem opções de frete para esse CEP. Fale com a loja.");
      } else {
        setShippingOptions([]);
        setShippingMode(data.is_local ? "local" : "estimate");
      }
    } catch (err) {
      toast.error("Não consegui calcular o frete. Tente de novo.");
    } finally {
      setShippingLoading(false);
    }
  };

  const applyCoupon = async () => {
    if (!couponInput.trim()) return;
    try {
      const { data } = await api.post("/coupons/validate", { code: couponInput.trim(), subtotal: total });
      setCoupon(data);
      toast.success(`Cupom ${data.code} aplicado!`);
    } catch {
      toast.error("Cupom inválido ou expirado");
      setCoupon(null);
    }
  };

  const createOrder = async () => {
    const { data } = await api.post("/orders", {
      items: items.map((i) => ({ product_id: i.id, name: i.name, price: i.price, qty: i.qty })),
      total: grandTotal,
      customer_name: customerName,
      customer_email: customerEmail,
      cep,
      shipping_name: shipping?.name || "",
      shipping_company: shipping?.company || "",
      shipping_price: shipping?.price || 0,
      shipping_days: shipping?.delivery_days || 0,
      coupon_code: coupon?.code || "",
    });
    return data;
  };

  const resetAfterCheckout = () => {
    clearCart();
    setCustomerName("");
    setCustomerEmail("");
    setCep("");
    setShipping(null);
    setShippingOptions([]);
    setShippingMode(null);
    setCoupon(null);
    setCouponInput("");
  };

  const checkout = async () => {
    if (items.length === 0) return;
    setSending(true);
    try {
      await createOrder();
    } catch {
      // O pedido ainda pode ser concluído pelo WhatsApp mesmo se o registro falhar
    }
    const lines = items.map((i) => `• ${i.qty}x ${i.name} — ${formatBRL(i.price * i.qty)}`);
    const message = [
      `Olá! Quero fazer um pedido${customerName ? ` (meu nome é ${customerName})` : ""}:`,
      "",
      ...lines,
      "",
      `Subtotal: ${formatBRL(total)}`,
      ...(shipping ? [`Frete (${shipping.name}${shipping.company ? ` · ${shipping.company}` : ""}): ${formatBRL(shipping.price)} — CEP ${cep}`] : []),
      ...(coupon ? [`Cupom ${coupon.code}: -${formatBRL(discount)}`] : []),
      `Total: ${formatBRL(grandTotal)}`,
    ].join("\n");
    window.open(buildWhatsAppLink(whatsappNumber, message), "_blank");
    toast.success("Pedido enviado! Continue a conversa no WhatsApp.");
    resetAfterCheckout();
    setSending(false);
    closeCart();
  };

  const payOnline = async () => {
    if (items.length === 0) return;
    if (!customerEmail || !customerEmail.includes("@")) {
      toast.error("Informe seu e-mail para pagar online");
      return;
    }
    setSending(true);
    try {
      const order = await createOrder();
      setMpOrder(order);
    } catch {
      toast.error("Não consegui criar o pedido. Tente novamente.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50" data-testid="cart-drawer">
      <div className="absolute inset-0 bg-black/40" onClick={closeCart} data-testid="cart-overlay" />
      <aside className="absolute right-0 top-0 h-full w-full max-w-md bg-[var(--site-bg)] shadow-2xl flex flex-col border-l border-[#E8E2D9]">
        <div className="flex items-center justify-between px-6 h-16 border-b border-[#E8E2D9]">
          <h2 className="font-display font-bold text-lg">Sua Sacola</h2>
          <button
            type="button"
            onClick={closeCart}
            data-testid="cart-close-button"
            className="p-2 rounded-full hover:bg-[#F2EDE4] transition-colors"
            aria-label="Fechar sacola"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {items.length === 0 ? (
            <p className="text-sm text-[#6B645C] mt-8 text-center" data-testid="cart-empty-message">
              Sua sacola está vazia. Explore o catálogo e adicione produtos!
            </p>
          ) : (
            <ul className="space-y-4">
              {items.map((item) => (
                <li key={item.id} className="flex gap-4 items-center bg-white rounded-2xl border border-[#E8E2D9] p-3" data-testid={`cart-item-${item.id}`}>
                  <div className="w-16 h-16 rounded-xl overflow-hidden bg-[#EDE6DB] shrink-0">
                    {item.image_url && <img src={resolveImageUrl(item.image_url)} alt={item.name} className="w-full h-full object-cover" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-display font-semibold text-sm truncate">{item.name}</p>
                    <p className="font-mono text-xs text-[#5A534C]">{formatBRL(item.price)}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <button
                        type="button"
                        onClick={() => updateQty(item.id, -1)}
                        data-testid={`cart-qty-minus-${item.id}`}
                        className="w-7 h-7 rounded-full border border-[#D5CDC2] flex items-center justify-center hover:border-[#1A1816] transition-colors"
                        aria-label="Diminuir quantidade"
                      >
                        <Minus className="w-3.5 h-3.5" />
                      </button>
                      <span className="font-mono text-sm w-6 text-center">{item.qty}</span>
                      <button
                        type="button"
                        onClick={() => updateQty(item.id, 1)}
                        data-testid={`cart-qty-plus-${item.id}`}
                        className="w-7 h-7 rounded-full border border-[#D5CDC2] flex items-center justify-center hover:border-[#1A1816] transition-colors"
                        aria-label="Aumentar quantidade"
                      >
                        <Plus className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeItem(item.id)}
                    data-testid={`cart-remove-${item.id}`}
                    className="p-2 text-[#6B645C] hover:text-[var(--brand)] transition-colors"
                    aria-label="Remover item"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {items.length > 0 && (
          <div className="border-t border-[#E8E2D9] px-6 py-5 space-y-3 overflow-y-auto max-h-[55vh]">
            <input
              type="text"
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
              placeholder="Seu nome (opcional)"
              data-testid="cart-customer-name-input"
              className="w-full bg-white border border-[#E8E2D9] rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-[var(--brand)] transition-colors"
            />
            {mpConfig.mercadopago_enabled && (
              <input
                type="email"
                value={customerEmail}
                onChange={(e) => setCustomerEmail(e.target.value)}
                placeholder="Seu e-mail (para pagar online)"
                data-testid="cart-customer-email-input"
                className="w-full bg-white border border-[#E8E2D9] rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-[var(--brand)] transition-colors"
              />
            )}

            {/* Frete */}
            <div className="bg-white rounded-2xl border border-[#E8E2D9] p-3.5 space-y-2">
              <p className="font-mono text-[11px] uppercase tracking-wider text-[#5A534C]">Entrega</p>
              <div className="flex gap-2">
                <input
                  type="text"
                  inputMode="numeric"
                  value={cep}
                  onChange={(e) => setCep(e.target.value.replace(/\D/g, "").slice(0, 8))}
                  placeholder="Seu CEP"
                  data-testid="cart-cep-input"
                  className="flex-1 bg-[var(--site-bg)] border border-[#E8E2D9] rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[var(--brand)] transition-colors"
                />
                <button
                  type="button"
                  onClick={calcShipping}
                  disabled={shippingLoading}
                  data-testid="cart-calc-shipping-button"
                  className="inline-flex items-center gap-1.5 bg-[#1A1816] text-white px-4 py-2 rounded-full font-display text-xs font-semibold hover:bg-[var(--brand)] transition-colors disabled:opacity-60"
                >
                  {shippingLoading && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                  Calcular
                </button>
              </div>
              {shippingMode === "live" && shippingOptions.length > 0 && (
                <div className="space-y-1.5 max-h-36 overflow-y-auto" data-testid="cart-shipping-options">
                  {shippingOptions.map((o) => (
                    <button
                      key={o.id}
                      type="button"
                      onClick={() => setShipping(o)}
                      data-testid={`cart-shipping-option-${o.id}`}
                      className={`w-full flex justify-between items-center text-left px-3 py-2 rounded-xl border text-xs transition-colors ${
                        shipping?.id === o.id ? "border-[var(--brand)] bg-[var(--brand)]/5" : "border-[#E8E2D9] hover:border-[#D5CDC2]"
                      }`}
                    >
                      <span className="text-[#5A534C]">
                        {o.name}{o.company ? ` · ${o.company}` : ""}
                        {o.delivery_days && <span className="block text-[11px] text-[#6B645C]">até {o.delivery_days} dias úteis</span>}
                      </span>
                      <span className="font-mono font-semibold text-[#1A1816]">{formatBRL(o.price)}</span>
                    </button>
                  ))}
                </div>
              )}
              {shippingMode === "local" && (
                <p className="text-xs text-green-700 bg-green-50 border border-green-200 rounded-xl px-3 py-2" data-testid="cart-shipping-local">
                  Você é da nossa cidade! A entrega é combinada pelo WhatsApp no final do pedido.
                </p>
              )}
              {shippingMode === "estimate" && (
                <p className="text-xs text-[#5A534C] bg-[var(--site-bg)] border border-[#E8E2D9] rounded-xl px-3 py-2" data-testid="cart-shipping-estimate">
                  Para esse CEP o frete é combinado pelo WhatsApp com o melhor valor.
                </p>
              )}
            </div>

            {/* Cupom */}
            <div className="flex gap-2">
              <input
                type="text"
                value={couponInput}
                onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                placeholder="Cupom de desconto"
                data-testid="cart-coupon-input"
                className="flex-1 bg-white border border-[#E8E2D9] rounded-xl px-4 py-2.5 text-sm uppercase focus:outline-none focus:border-[var(--brand)] transition-colors"
              />
              <button
                type="button"
                onClick={applyCoupon}
                data-testid="cart-apply-coupon-button"
                className="bg-[#F2EDE4] border border-[#D5CDC2] px-4 py-2 rounded-full font-display text-xs font-semibold hover:border-[#1A1816] transition-colors"
              >
                Aplicar
              </button>
            </div>
            {coupon && (
              <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-xl px-3 py-2" data-testid="cart-coupon-applied">
                <span className="text-xs text-green-700">Cupom {coupon.code} aplicado</span>
                <button type="button" onClick={() => { setCoupon(null); setCouponInput(""); }} data-testid="cart-remove-coupon-button" className="text-green-700 hover:text-red-600 transition-colors" aria-label="Remover cupom">
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}

            {/* Totais */}
            <div className="space-y-1 pt-1" data-testid="cart-totals">
              <div className="flex justify-between text-sm text-[#5A534C]">
                <span>Subtotal</span>
                <span className="font-mono">{formatBRL(total)}</span>
              </div>
              {shipping && (
                <div className="flex justify-between text-sm text-[#5A534C]">
                  <span>Frete ({shipping.name}{shipping.company ? ` · ${shipping.company}` : ""})</span>
                  <span className="font-mono">{formatBRL(shipping.price)}</span>
                </div>
              )}
              {coupon && discount > 0 && (
                <div className="flex justify-between text-sm text-green-700">
                  <span>Desconto ({coupon.code})</span>
                  <span className="font-mono">-{formatBRL(discount)}</span>
                </div>
              )}
              <div className="flex justify-between items-baseline border-t border-[#E8E2D9] pt-2">
                <span className="text-sm text-[#5A534C]">Total</span>
                <span className="font-display font-bold text-xl" data-testid="cart-total">{formatBRL(grandTotal)}</span>
              </div>
            </div>

            <button
              type="button"
              onClick={() => setShowSim(true)}
              data-testid="cart-simulate-button"
              className="w-full inline-flex items-center justify-center gap-2 border border-[#D5CDC2] text-[#1A1816] py-3 rounded-full font-display text-sm font-semibold hover:border-[var(--brand)] hover:text-[var(--brand)] transition-colors"
            >
              <Calculator className="w-4 h-4" />
              Simular Parcelas desse Valor
            </button>

            {mpConfig.mercadopago_enabled && (
              <button
                type="button"
                onClick={payOnline}
                disabled={sending}
                data-testid="cart-pay-online-button"
                className="w-full inline-flex items-center justify-center gap-2 bg-[#1A1816] text-white py-3.5 rounded-full font-display text-sm font-semibold hover:bg-[#332F2C] transition-colors disabled:opacity-60"
              >
                <CreditCard className="w-4 h-4" />
                {sending ? "Aguarde..." : "Pagar Agora (Pix ou Cartão)"}
              </button>
            )}
            <button
              type="button"
              onClick={checkout}
              disabled={sending}
              data-testid="cart-checkout-whatsapp-button"
              className="w-full inline-flex items-center justify-center gap-2 bg-[var(--brand)] text-white py-3.5 rounded-full font-display text-sm font-semibold hover:bg-[var(--brand-dark)] transition-colors disabled:opacity-60"
            >
              <WhatsAppIcon />
              {sending ? "Enviando..." : "Finalizar Pedido no WhatsApp"}
            </button>
            <p className="text-[11px] text-[#6B645C] text-center">
              Pague online com Pix ou cartão, ou finalize pelo WhatsApp com a loja.
            </p>
          </div>
        )}
      </aside>
      {mpOrder && (
        <CheckoutMP
          order={mpOrder}
          publicKey={mpConfig.mp_public_key}
          whatsappNumber={whatsappNumber}
          onClose={() => setMpOrder(null)}
          onPaid={() => {
            clearCart();
            setCustomerName("");
            setCustomerEmail("");
          }}
        />
      )}
      {showSim && (
        <div className="fixed inset-0 z-[65] flex items-end sm:items-center justify-center" data-testid="cart-sim-modal">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowSim(false)} />
          <div className="relative w-full max-w-md max-h-[85vh] overflow-y-auto rounded-t-3xl sm:rounded-3xl bg-[var(--site-bg)] p-2">
            <button
              type="button"
              onClick={() => setShowSim(false)}
              data-testid="cart-sim-close"
              className="absolute top-4 right-4 z-10 p-2 rounded-full bg-white/80 hover:bg-white transition-colors"
              aria-label="Fechar simulador"
            >
              <X className="w-5 h-5" />
            </button>
            <InstallmentSimulator initialAmount={grandTotal.toFixed(2).replace(".", ",")} />
          </div>
        </div>
      )}
    </div>
  );
}
